import { findMoveById } from '../data/moves';
import { getFollowerTemplate } from '../data/followers';
import type { MoveRef } from '../schemas/character';
import type { ClassId } from '../schemas/classId';
import {
  FollowerSchema,
  type Follower,
  type FollowerCost,
  type FollowerModifiers,
  type FollowerTemplate,
} from '../schemas';
import type { StatId } from '../schemas/stats';
import { computeFollowerMaxHp } from './followerStats';
import { applyPeculiarities, mergeSkills } from './followerPeculiarities';
import {
  applyPrimarySkillFromNarrativeField,
  applyTraitFromNarrativeFields,
} from './followerNarrativeFields';
import { applyUpgrades } from './followerUpgrades';
import { enrichGroupsWithMainShorthand, isFollowerSetupComplete } from './followerSetupComplete';

// Re-export per backward compat con consumer che importavano dal modulo orig.
export { applyUpgrades } from './followerUpgrades';
export type { UpgradeApplicationResult } from './followerUpgrades';
// D8c — re-export del setup-complete API per consumer pre-D8c.
export {
  getEffectivePickCount,
  getPickCountForOutcome,
  enrichGroupsWithMainShorthand,
  isFollowerSetupComplete,
} from './followerSetupComplete';

/**
 * Service di **istanziazione** di un Follower a partire dal suo
 * `FollowerTemplate` + le scelte del giocatore compilate sul `MoveRef`
 * della mossa-source (FI-8a).
 *
 * Flusso:
 *  1. Il PG sceglie una mossa con `followerTemplateId` (es. *Compagno
 *     Animale*) nel builder.
 *  2. Il `MoveSetupEditor` compila `MoveRef.selectedChoiceGroups` +
 *     `MoveRef.narrativeFieldValues`.
 *  3. Quando i campi richiesti sono completi, il reducer chiama
 *     `buildFollowerFromTemplate(template, selections, character)` per
 *     ottenere un `Follower` valido da salvare in `Character.followers`.
 */

/**
 * Input del giocatore al momento della creazione del Follower.
 * Estratto da `MoveRef.selectedChoiceGroups` + `MoveRef.narrativeFieldValues`
 * della mossa-source.
 */
export interface FollowerCreationInput {
  /** Mappa `ChoiceGroup.id → IDs selezionati` (riprende `MoveRef.selectedChoiceGroups`). */
  selectedChoiceGroups: Readonly<Record<string, readonly string[]>>;
  /** Mappa `NarrativeField.id → testo` (riprende `MoveRef.narrativeFieldValues`). */
  narrativeFieldValues: Readonly<Record<string, string>>;
  /** Livello del PG, usato per `hpRule.scaling`. */
  characterLevel: number;
  /** ID univoco da assegnare al nuovo Follower (di solito UUID). */
  newFollowerId: string;
  /**
   * **D8b**: ID di tutte le mosse possedute dal PG (`Character.moves[*].id`).
   * Usato da `applyUpgrades` per applicare gli override di `template.upgrades`
   * quando la mossa di upgrade è presente (es. Khan dei Khan modifica la
   * Banda di Guerra). Opzionale per retrocompat con i 4 template FI-8a/b/c/d
   * che non hanno upgrades.
   */
  ownedMoveIds?: readonly string[];
  /**
   * **D8c — FI-41**: `MoveRef` delle mosse di upgrade indicizzati per `id`.
   * Necessario per leggere `selectedChoiceGroups` e `narrativeFieldValues`
   * delle mosse advanced che applicano un upgrade con scelte del giocatore
   * (es. *Servitore Competente* Warlock) o trait sempre presenti
   * (es. *Ben Addestrato* Ranger). Opzionale per retrocompat con D8b
   * (upgrade statici di Khan dei Khan non leggono dal MoveRef).
   */
  upgradeMoveRefs?: ReadonlyMap<string, MoveRef>;
}

const STAT_IDS_TO_KEY: Record<string, keyof FollowerModifiers> = {
  forza: 'forza',
  destrezza: 'destrezza',
  costituzione: 'costituzione',
  intelligenza: 'intelligenza',
  saggezza: 'saggezza',
  carisma: 'carisma',
};

function pickFirstSelection(
  selections: Readonly<Record<string, readonly string[]>>,
  groupId: string,
): string | undefined {
  return selections[groupId]?.[0];
}

/**
 * Costruisce un `Follower` valido da template + scelte giocatore.
 *
 * Mapping applicato in ordine:
 *  - `template.modifierBonus` → sostituisce il mod base con `amount` per lo
 *    stat scelto dal giocatore (es. Compagno Animale: tutti +1, ma `+2` su
 *    quello scelto)
 *  - `template.archetypeFromChoiceGroup` → `Follower.archetype` = id scelto
 *  - `template.traitsFromChoiceGroup` → aggiunge il trait mappato all'id
 *    scelto in `Follower.traits`
 *  - `template.nameFromNarrativeField` → `Follower.name` = valore del field
 *  - `template.fixedSkills` / `template.fixedTraits` → copiati direttamente
 *  - `template.capabilityCaps` → snapshot in `Follower.caps`
 *  - `currentHp` inizializzato al `maxHp` calcolato
 *
 * Lancia (con messaggio descrittivo) se mancano scelte richieste dal template
 * o se i valori non corrispondono a opzioni note.
 *
 * Il risultato è validato via `FollowerSchema.parse()` (sentinel).
 */
export function buildFollowerFromTemplate(
  template: FollowerTemplate,
  input: FollowerCreationInput,
): Follower {
  // D8b → D8c: applica upgrade del template (statici + additivi choice-based).
  // Restituisce `effectiveTemplate` post-statici + delta runtime (trait /
  // skill / modifier) da accumulare nel Follower finale.
  const upgradeResult = applyUpgrades(template, input.ownedMoveIds ?? [], input.upgradeMoveRefs);
  const { effectiveTemplate } = upgradeResult;

  const baseModifiers = applyModifierBonus(effectiveTemplate, input);
  const archetype = applyArchetype(effectiveTemplate, input);
  const baseTraits = applyTraits(effectiveTemplate, input);
  const peculiarities = applyPeculiarities(effectiveTemplate, input, baseModifiers);
  // D8c: applica modifier delta da peculiarities choice-based degli upgrade
  // (override dei modifier già calcolati da template + peculiarities-base).
  const modifiers = { ...peculiarities.modifiers, ...upgradeResult.additionalModifiers };
  // D8b: capacità utile sempre presente da narrativeField (Braccio Destro).
  const alwaysTraits = applyTraitFromNarrativeFields(effectiveTemplate, input.narrativeFieldValues);
  // D8c: trait runtime aggiunti dagli upgrade (Ben Addestrato/Alleato Innaturale
  // additionalAlwaysTraitsFromNarrativeFields + custom-trait dalle peculiarities
  // choice-based di Servitore Competente/Maestria).
  const traits = [
    ...baseTraits,
    ...peculiarities.traitAdditions,
    ...alwaysTraits,
    ...upgradeResult.additionalTraits,
  ];
  // D8b: Abilità primaria scelta dal giocatore via narrativeField oneOf (Braccio Destro).
  const primaryFromNarrative = applyPrimarySkillFromNarrativeField(
    effectiveTemplate,
    input.narrativeFieldValues,
  );
  // D8c: skill aggiunte dagli upgrade choice-based (skill-rank-from-narrative
  // di Servitore Competente/Maestria). Fuse via `mergeSkills` con max-rank:
  // se Maestria porta una sec a R2 e quella sec esisteva già a R1, il merge
  // produce R2 della stessa abilityId.
  const skills = mergeSkills(
    [...effectiveTemplate.fixedSkills, ...primaryFromNarrative],
    [...peculiarities.skillAdditions, ...upgradeResult.additionalSkills],
  );
  const name = applyName(effectiveTemplate, input);

  // D8a: bonus scaling HP (peculiarità "resistente") + tag intrinseci da archetype.
  const currentHp = computeFollowerMaxHp(
    effectiveTemplate.baseStats.hpRule,
    input.characterLevel,
    traits,
    peculiarities.hpBonusScaling,
  );
  const archetypeTags = resolveArchetypeTags(effectiveTemplate, input);
  const stats = {
    ...effectiveTemplate.baseStats,
    modifiers,
    tags: [...effectiveTemplate.baseStats.tags, ...archetypeTags],
  };
  const cost = buildCost(effectiveTemplate, peculiarities.costSuspended);

  const candidate: Follower = {
    id: input.newFollowerId,
    sourceMoveId: template.sourceMoveId,
    templateId: template.id,
    type: effectiveTemplate.type,
    name,
    archetype,
    stats,
    loyalty: effectiveTemplate.defaultLoyalty,
    cost,
    skills,
    traits,
    inventory: [],
    currentHp,
    isAlive: true,
    respawnRule: effectiveTemplate.respawnRule,
    caps: effectiveTemplate.capabilityCaps,
    // D8a: salva il bonus scaling come snapshot per re-calcolo runtime del maxHp.
    hpBonusScaling: peculiarities.hpBonusScaling,
    notes: '',
  };

  return FollowerSchema.parse(candidate);
}

// `applyUpgrades` estratto in `./followerUpgrades.ts` (D8b, max-lines: 400).

/**
 * D8a — costruisce il `Follower.cost` dal template + flag `costSuspended`
 * accumulato dalle peculiarità (`cost-suspension` kind). Se il template non
 * ha `costMotivation`, ritorna `undefined` (non c'è Costo da sospendere).
 */
function buildCost(template: FollowerTemplate, costSuspended: boolean): FollowerCost | undefined {
  if (!template.costMotivation) return undefined;
  return {
    motivation: template.costMotivation,
    suspended: costSuspended,
    paidThisSession: false,
  };
}

/**
 * D8a — risolve i tag intrinseci derivati dalla scelta archetype (Druido
 * Supporto Elementale: elemento → 3 tag). Ritorna array vuoto se il template
 * non ha `archetypeTagBundle` o se l'opzione scelta non è mappata.
 */
function resolveArchetypeTags(
  template: FollowerTemplate,
  input: FollowerCreationInput,
): readonly string[] {
  const bundle = template.archetypeTagBundle;
  if (!bundle) return [];
  const selected = pickFirstSelection(input.selectedChoiceGroups, bundle.fromChoiceGroup);
  if (selected === undefined) return [];
  return bundle.optionToTags[selected] ?? [];
}

// `applyPrimarySkillFromNarrativeField` + `applyTraitFromNarrativeFields`
// estratti in `./followerNarrativeFields.ts` (D8b, max-lines: 400).

// -- Helper di applicazione delle mappature template → Follower --

function readNarrativeField(values: Readonly<Record<string, string>>, fieldId: string): string {
  return values[fieldId]?.trim() ?? '';
}

function applyModifierBonus(
  template: FollowerTemplate,
  input: FollowerCreationInput,
): FollowerModifiers {
  const modifiers: FollowerModifiers = { ...template.baseStats.modifiers };
  if (!template.modifierBonus) return modifiers;
  const selectedStatId = pickFirstSelection(
    input.selectedChoiceGroups,
    template.modifierBonus.fromChoiceGroup,
  );
  if (selectedStatId === undefined) {
    throw new Error(
      `Template '${template.id}': scelta mancante per ChoiceGroup '${template.modifierBonus.fromChoiceGroup}' (modifier bonus)`,
    );
  }
  const statKey = STAT_IDS_TO_KEY[selectedStatId];
  if (statKey === undefined) {
    throw new Error(
      `Template '${template.id}': opzione '${selectedStatId}' non corrisponde a uno StatId valido (ammessi: forza, destrezza, costituzione, intelligenza, saggezza, carisma)`,
    );
  }
  modifiers[statKey] = template.modifierBonus.amount;
  return modifiers;
}

function applyArchetype(
  template: FollowerTemplate,
  input: FollowerCreationInput,
): string | undefined {
  if (!template.archetypeFromChoiceGroup) return undefined;
  const selected = pickFirstSelection(
    input.selectedChoiceGroups,
    template.archetypeFromChoiceGroup,
  );
  if (selected === undefined) {
    throw new Error(
      `Template '${template.id}': scelta mancante per ChoiceGroup '${template.archetypeFromChoiceGroup}' (archetype)`,
    );
  }
  const customArch = template.archetypeCustomFromNarrativeField;
  if (customArch?.fromChoiceGroup !== template.archetypeFromChoiceGroup) return selected;
  if (customArch.whenChoice !== selected) return selected;
  const customValue = readNarrativeField(input.narrativeFieldValues, customArch.narrativeFieldId);
  if (customValue.length === 0) {
    throw new Error(
      `Template '${template.id}': scelta '${selected}' richiede NarrativeField '${customArch.narrativeFieldId}' compilato (archetype custom)`,
    );
  }
  return customValue;
}

function applyTraits(
  template: FollowerTemplate,
  input: FollowerCreationInput,
): FollowerTemplate['fixedTraits'][number][] {
  const traits = [...template.fixedTraits];
  const mapping = template.traitsFromChoiceGroup;
  if (!mapping) return traits;
  const selected = pickFirstSelection(input.selectedChoiceGroups, mapping.fromChoiceGroup);
  if (selected === undefined) {
    throw new Error(
      `Template '${template.id}': scelta mancante per ChoiceGroup '${mapping.fromChoiceGroup}' (traits)`,
    );
  }
  const customTrait = template.traitCustomFromNarrativeField;
  if (
    customTrait?.fromChoiceGroup === mapping.fromChoiceGroup &&
    customTrait.whenChoice === selected
  ) {
    traits.push(buildCustomTrait(template, input, customTrait, selected));
    return traits;
  }
  const trait = mapping.optionToTrait[selected];
  if (trait === undefined) {
    throw new Error(
      `Template '${template.id}': opzione '${selected}' non ha un trait corrispondente in optionToTrait`,
    );
  }
  traits.push(trait);
  return traits;
}

function buildCustomTrait(
  template: FollowerTemplate,
  input: FollowerCreationInput,
  customTrait: NonNullable<FollowerTemplate['traitCustomFromNarrativeField']>,
  selected: string,
): FollowerTemplate['fixedTraits'][number] {
  const label = readNarrativeField(input.narrativeFieldValues, customTrait.labelFieldId);
  if (label.length === 0) {
    throw new Error(
      `Template '${template.id}': scelta '${selected}' richiede NarrativeField '${customTrait.labelFieldId}' compilato (trait custom label)`,
    );
  }
  const description = customTrait.descriptionFieldId
    ? readNarrativeField(input.narrativeFieldValues, customTrait.descriptionFieldId)
    : '';
  return {
    id: `custom-${customTrait.fromChoiceGroup}`,
    label,
    ...(description.length > 0 ? { description } : {}),
  };
}

function applyName(template: FollowerTemplate, input: FollowerCreationInput): string {
  if (!template.nameFromNarrativeField) return template.label;
  const value = readNarrativeField(input.narrativeFieldValues, template.nameFromNarrativeField);
  if (value.length === 0) {
    throw new Error(
      `Template '${template.id}': nome mancante dal NarrativeField '${template.nameFromNarrativeField}'`,
    );
  }
  return value;
}

// ---------------------------------------------------------------------------
// Derivazione runtime dei Follower dalle mosse del PG
// ---------------------------------------------------------------------------

/**
 * Deriva i `Follower` correnti del PG dalle mosse possedute. Ritorna solo
 * i Follower per cui il setup della mossa-source è completo (tutti i
 * `choiceGroups` selezionati e i `narrativeFields` required compilati).
 *
 * Le mosse con `followerTemplateId` ma setup incompleto sono saltate
 * silenziosamente (il giocatore le sta ancora compilando — la sezione
 * `'starting-moves'` blocca il finalize finché tutto è OK).
 *
 * **Pattern SSOT**: niente sync nel reducer; il `Character.followers` è
 * **derivato runtime** da `Character.moves` (analogo a `getActiveSpellLists`).
 * Il `finalizeCharacter` chiama questa funzione e congela il risultato
 * dentro `Character.followers`.
 *
 * @param classId classe del PG (per il lookup delle mosse nel catalogo)
 * @param moves mosse possedute (`Character.moves` o `CharacterDraft.moves`)
 * @param characterLevel livello del PG per `hpRule.scaling`
 * @param idFor funzione che ritorna l'ID univoco da assegnare al Follower
 *   dato l'ID della mossa-source. Default: stessa stringa del moveId (così
 *   il follower derivato è stabile tra render — chiave riproducibile)
 */
export function getFollowersFromDraft(
  classId: ClassId,
  moves: readonly MoveRef[],
  characterLevel: number,
  idFor: (moveId: string) => string = (moveId) => `follower-${moveId}`,
): Follower[] {
  const followers: Follower[] = [];
  // D8b: lista delle mosse possedute dal PG, condivisa fra tutti i Follower
  // derivati. Permette a `applyUpgrades` di sapere se una mossa di upgrade
  // (es. Khan dei Khan) è in possesso e quindi va applicata.
  const ownedMoveIds = moves.map((m) => m.id);
  // D8c — FI-41: lookup `MoveRef` per id, usato per leggere `selectedChoiceGroups`
  // e `narrativeFieldValues` delle mosse di upgrade quando queste hanno
  // scelte del giocatore (Servitore Competente, Maestria) o trait sempre
  // presenti (Ben Addestrato, Alleato Innaturale).
  const moveRefsById = new Map(moves.map((m) => [m.id, m]));
  for (const moveRef of moves) {
    const move = findMoveById(classId, moveRef.id);
    if (!move?.followerTemplateId) continue;
    const template = getFollowerTemplate(move.followerTemplateId);
    if (!template) continue;
    const upgradeMoveRefs = collectUpgradeMoveRefs(template, moveRefsById);
    if (!isFollowerSetupComplete(moveRef, template, upgradeMoveRefs)) continue;
    try {
      const enrichedGroups = enrichGroupsWithMainShorthand(
        moveRef.selectedChoiceGroups,
        moveRef.selectedChoices ?? [],
      );
      const follower = buildFollowerFromTemplate(template, {
        selectedChoiceGroups: enrichedGroups,
        narrativeFieldValues: moveRef.narrativeFieldValues ?? {},
        characterLevel,
        newFollowerId: idFor(move.id),
        ownedMoveIds,
        upgradeMoveRefs,
      });
      followers.push(follower);
    } catch {
      // Il setup non è completo o ha valori invalidi — il predicate della
      // sezione `'starting-moves'` bloccherà comunque il finalize. Skip
      // silenzioso per evitare crash durante l'editing live.
      continue;
    }
  }
  return followers;
}

/**
 * D8c — FI-41: estrae i `MoveRef` delle mosse di upgrade del template che
 * sono effettivamente possedute dal PG. Solo gli upgrade con `MoveRef`
 * disponibile possono applicare i campi D8c (`peculiaritiesFromChoiceGroup`
 * + `additionalAlwaysTraitsFromNarrativeFields`); gli upgrade D8b puramente
 * statici (Khan dei Khan) non hanno bisogno del `MoveRef`.
 */
function collectUpgradeMoveRefs(
  template: FollowerTemplate,
  moveRefsById: ReadonlyMap<string, MoveRef>,
): ReadonlyMap<string, MoveRef> {
  const map = new Map<string, MoveRef>();
  for (const upgrade of template.upgrades) {
    const ref = moveRefsById.get(upgrade.fromMoveId);
    if (ref) map.set(upgrade.fromMoveId, ref);
  }
  return map;
}

// D8c — funzioni di setup-complete (isFollowerSetupComplete, getEffectivePickCount,
// enrichGroupsWithMainShorthand, getPickCountForOutcome) estratte in
// `followerSetupComplete.ts` per rispettare `max-lines: 400`.

// Re-export del tipo StatId per coerenza con altre API di service.
export type { StatId };
